import type { StopListSettingsModel } from '@/shared/generated'

import React from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { getQueryDescription } from '@/shared/components/query-builder'
import { Skeleton } from '@/shared/components/ui/skeleton'

import { useStopListSettingsData } from '../lib/useStopListSettingsData'

export function StopListCustomRulesView({
	settings,
	compact = false,
}: {
	settings?: StopListSettingsModel
	compact?: boolean
}) {
	const { t } = useTranslation()

	const { fields, dataChoices, isLoading } = useStopListSettingsData()

	return (
		<div
			className={
				compact ? 'line-clamp-2 space-x-2 text-sm' : 'flex flex-col gap-2'
			}
		>
			{isLoading ? (
				<Skeleton className='h-8 max-w-56 animate-pulse rounded-lg' />
			) : settings?.custom_rules.length ? (
				getQueryDescription(settings?.custom_rules, {
					fields,
					dataChoices,
				})?.map((item, index) =>
					compact ? (
						<StoplistRuleRowCompact
							key={`${item?.label}-${item?.operation}`}
							item={item}
						/>
					) : (
						<StoplistRuleRow
							key={`${item?.label}-${item?.operation}`}
							item={item}
							index={index}
						/>
					)
				)
			) : (
				<span className='text-sm text-muted-foreground'>
					{t('scenario.settigns_tab.stop_list.custom_rules.no_rules', {
						defaultValue: 'No custom rules currently applied to this scenario.',
					})}
				</span>
			)}
		</div>
	)
}
const StoplistRuleRowCompact = ({
	item,
}: {
	item: { label: string; operation: string; value: string }
}) => (
	<div
		key={`${item?.label}-${item?.operation}`}
		className='rounded-sm text-sm break-all'
	>
		<span className='px-1 font-semibold'>{item?.label}</span>
		<span className='rounded-sm px-1'>{item?.operation}</span>
		<span className='rounded-sm px-1 font-medium text-muted-foreground'>
			{item?.value}
		</span>
	</div>
)
const StoplistRuleRow = ({
	item,
	index,
}: {
	item: { label: string; operation: string; value: string }
	index: number
}) => {
	const { t } = useTranslation()

	return (
		<div
			key={`${item?.label}-${item?.operation}`}
			className='flex flex-wrap items-center gap-2'
		>
			<span className='shrink-0 text-sm whitespace-nowrap'>
				{t(index === 0 ? 'where' : 'or')}
			</span>
			<span className='shrink-0 text-sm font-semibold whitespace-nowrap'>
				{item?.label}
			</span>
			<span className='shrink-0 text-sm font-semibold whitespace-nowrap'>
				{item?.operation &&
					t(item?.operation, { defaultValue: item?.operation })}
			</span>
			<span className='max-w-full min-w-0 text-sm font-semibold break-words'>
				{item?.value === 'true' ? (
					<CheckIcon className='size-4 shrink-0' />
				) : item?.value === 'false' ? (
					<XIcon className='size-4 shrink-0' />
				) : Array.isArray(item?.value) ? (
					item?.value.join(', ')
				) : (
					item?.value
				)}
			</span>
		</div>
	)
}
